/**
 * Polls /api/demos/status every 30 s while the tab is visible (WQ-348). Pauses when the
 * tab is hidden and refreshes as soon as it is visible again.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { getDemoStatus, type DemoStatusResult } from '../services/demoStatusService';

type AuthFetch = (url: string, options?: RequestInit) => Promise<Response>;

export const DEMO_STATUS_REFRESH_MS = 30_000;

export function useDemoStatus(authFetch: AuthFetch, { enabled = true }: { enabled?: boolean } = {}) {
  const [result, setResult] = useState<DemoStatusResult | null>(null);
  const alive = useRef(true);

  const refresh = useCallback(async () => {
    const next = await getDemoStatus(authFetch);
    if (alive.current) setResult(next);
  }, [authFetch]);

  useEffect(() => {
    alive.current = true;
    if (!enabled) return () => { alive.current = false; };
    let timer: ReturnType<typeof setInterval> | null = null;
    const start = () => {
      if (timer === null) timer = setInterval(refresh, DEMO_STATUS_REFRESH_MS);
    };
    const stop = () => {
      if (timer !== null) clearInterval(timer);
      timer = null;
    };
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') {
        stop();
      } else {
        refresh();
        start();
      }
    };
    if (document.visibilityState !== 'hidden') {
      refresh();
      start();
    }
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      alive.current = false;
      stop();
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [enabled, refresh]);

  return { result, refresh };
}
